import { lazy, Suspense, useMemo } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import {
  GlobalSnackbarContext,
  useGlobalSnackbar,
} from 'web-library/stories/atom/SnackBar/GlobalSnackbar';
import Box from 'web-library/stories/atom/Box/Box';
import TwoCubes from './assets/bg_size_lg.svg';
import RouteKey from './routes/key';
import ScrollToTop from './hooks/scrollToTop';
import { useBrowserDetection } from './hooks/useBrowserDetection';
import { useInactivitySession } from './hooks/useInactivitySession';
import { useUserDetails } from './hooks/userDetails';
import { selectEmptyState } from './store/appSlice';
import { useAuth } from './modules/profile/logic/auth';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AdvisoryBannerContent from './modules/announcement/components/AdvisoryBannerContent';
import { UserDetailsResponse } from './api-refactored/user/useGetUserDetails';
import { AxiosResponse } from 'axios';

const Footer = lazy(() => import('./components/Footer'));
const GlobalSnackbar = lazy(() => import('web-library/stories/atom/SnackBar/GlobalSnackbar'));
const AuthRoute = lazy(() => import('./routes/AuthRoute'));
const RootRouter = lazy(() => import('./routes/RootRouter'));
const Singpass = lazy(() => import('./modules/profile/components/SingPass'));
const MIMS = lazy(() => import('./modules/profile/components/MIMS'));
const MyInfoPage = lazy(() => import('./modules/profile/components/MyInfoPage'));
const PaymentLinkRoute = lazy(() => import('./routes/PaymentLinkRoute'));
const ENetsPaymentInterimResultPage = lazy(
  () => import('./pages/payment/ENetsPaymentInterimResultPage')
);
const NotificationAcknowledgementRoute = lazy(
  () => import('./routes/NotificationAcknowledgementRoute')
);
const UnSupportedBrowser = lazy(() => import('./pages/error/UnSupportedBrowser'));
const DefaultHeaderEmpty = lazy(() => import('./components/DefaultHeaderEmpty'));
const InactivitySession = lazy(() => import('./modules/session/components/InactivitySession'));
const Image = lazy(() => import('web-library/stories/atom/Image/Image'));
const SchoolCandidatePaymentRoute = lazy(() => import('./routes/SchoolCandidatePaymentRoute'));

const useStyles = makeStyles()((theme) => ({
  outerBox: {
    overflowX: 'hidden',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.color.background.bgMain,
  },
  appContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  displayHeight: {
    minHeight: '100vh',
  },
  twoCubes: {
    position: 'absolute',
    right: 0,
    zIndex: 0,
    width: 386,
    [theme.breakpoints.down('lg')]: {
      width: 254,
    },
    [theme.breakpoints.down('md')]: {
      width: 154,
    },
  },
}));

const {
  auth: authRoute,
  myinfo,
  singpass,
  mims,
  eNetsInterimResult,
  paymentLink,
  notSupport,
  notificationAcknowledgement,
  payment,
} = RouteKey.child;

const { unSupportedBrowser } = RouteKey.error.child;

function App() {
  const { classes } = useStyles();
  const { isAllowed } = useBrowserDetection();

  const snackbar = useGlobalSnackbar();

  const { open, setOpen } = useInactivitySession();
  const showTwoCubes = useSelector(selectEmptyState);
  const { isLoading, isSuccess, data } = useUserDetails();

  const snackbarVal = useMemo(() => ({ snackbarHandler: snackbar.snackbarHandler }), [
    snackbar.snackbarHandler,
  ]);

  const auth = useAuth();

  const isLoggedInUser = (res: AxiosResponse<UserDetailsResponse> | undefined): boolean => {
    return (
      res?.status === 200 &&
      Array.isArray(res.data?.roles) &&
      res.data.roles.length > 0 &&
      typeof res.data.displayName === 'string' &&
      res.data.displayName.trim() !== ''
    );
  };

  return !isLoading ? (
    <>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <GlobalSnackbarContext.Provider value={snackbarVal}>
          {isSuccess && isLoggedInUser(data) && <AdvisoryBannerContent />}
          <ScrollToTop />
          <Box
            className={[classes.outerBox, auth.isUserLoggedIn ? classes.displayHeight : ''].join(
              ' '
            )}
          >
            {/*[EPPOPNKHJV-2678]: WOGAA Accessibility: fix "Document does not have a main landmark" on candidate routes. */}
            <Box
              component="main"
              className={[
                classes.appContent,
                !auth.isUserLoggedIn ? classes.displayHeight : '',
              ].join(' ')}
            >
              {!isAllowed && <Redirect to={`/${notSupport}/${unSupportedBrowser}`} />}
              {isAllowed && (
                <Suspense fallback={null}>
                  <Switch>
                    <Route path={`/${singpass}`}>
                      <Singpass />
                    </Route>

                    <Route path={`/${myinfo}`}>
                      <MyInfoPage />
                    </Route>

                    <Route path={`/${mims}`}>
                      <MIMS />
                    </Route>

                    <Route path={`/${eNetsInterimResult}`}>
                      <DefaultHeaderEmpty />
                      <ENetsPaymentInterimResultPage />
                    </Route>

                    <Route path={`/${paymentLink}`}>
                      <PaymentLinkRoute />
                    </Route>

                    <Route path={`/${payment}`}>
                      <AdvisoryBannerContent />
                      <SchoolCandidatePaymentRoute />
                    </Route>

                    <Route path={`/${notificationAcknowledgement}`}>
                      <NotificationAcknowledgementRoute />
                    </Route>

                    <Route path={`/${authRoute}`}>
                      <AuthRoute />
                    </Route>

                    <Route path="/">
                      <RootRouter data={data?.data} isSuccess={isSuccess} />
                    </Route>
                  </Switch>
                </Suspense>
              )}
              {!isAllowed && (
                <Route exact path="/">
                  <Redirect to={`/${notSupport}/${unSupportedBrowser}`} />
                </Route>
              )}
              <Route path={`/${notSupport}/${unSupportedBrowser}`}>
                <Suspense fallback={null}>
                  <UnSupportedBrowser />
                </Suspense>
              </Route>
              {showTwoCubes && (
                <Suspense fallback={null}>
                  <Image className={classes.twoCubes} src={TwoCubes} alt="cube pic" />
                </Suspense>
              )}
            </Box>

            <Suspense fallback={null}>
              <Footer />
            </Suspense>
            {open && (
              <Suspense fallback={null}>
                <InactivitySession open={open} setOpen={setOpen} />
              </Suspense>
            )}
          </Box>

          {snackbar.snackbarHandler.snackbarOpenState && (
            //EPPOPNKHJV-1906: add custom autoHideDuration
            <Suspense fallback={null}>
              <GlobalSnackbar
                severity={snackbar.severity}
                data={snackbar.data}
                anchorOrigin={snackbar.anchorOrigin}
                handler={{
                  open: snackbar.snackbarHandler.snackbarOpenState,
                  handleClose: snackbar.snackbarHandler.handleClose,
                }}
                autoHideDuration={snackbar.autoHideDuration}
              />
            </Suspense>
          )}
        </GlobalSnackbarContext.Provider>
      </LocalizationProvider>
    </>
  ) : null;
}

export default App;
